import { useEffect } from 'react';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

export const useSchemaOrg = (schemaData: SchemaOrgData | SchemaOrgData[]) => {
  useEffect(() => {
    const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];
    const scriptIds: string[] = [];

    schemas.forEach((schema, index) => {
      const scriptId = `schema-org-${index}-${Date.now()}`;
      scriptIds.push(scriptId);

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      scriptIds.forEach(id => {
        const script = document.getElementById(id);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, [schemaData]);
};

export const getLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cheryleaf.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: 'Chery Leaf',
    alternateName: 'Chery Leaf Norwich Coffee Shop',
    description: 'Norwich\'s premier coffee shop serving artisan coffee, breakfast, and fresh pastries in the city centre',
    image: `${siteUrl}/og-image.jpg`,
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+441603699955',
    email: 'hello@cherryleafnorwich.co.uk',
    priceRange: '££',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '50 St Giles Street',
      addressLocality: 'Norwich',
      addressRegion: 'Norfolk',
      postalCode: 'NR2 1LW',
      addressCountry: 'GB'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 52.6309,
      longitude: 1.2974
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:30',
        closes: '16:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '16:00'
      }
    ],
    servesCuisine: 'Coffee, Breakfast, Pastries, Beverages',
    menu: `${siteUrl}/#menu`,
    areaServed: {
      '@type': 'City',
      name: 'Norwich',
      '@id': 'https://en.wikipedia.org/wiki/Norwich'
    },
    sameAs: [
      'https://www.instagram.com/cherryleaf_norwich',
      'https://x.com/cherryleafcoffe'
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '150'
    }
  };
};

export const getWebsiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cheryleaf.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Chery Leaf',
    url: siteUrl,
    description: 'Norwich\'s premier coffee shop offering artisanal coffee, freshly baked pastries, and specialty drinks.',
    publisher: {
      '@type': 'Organization',
      name: 'Chery Leaf',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`
      }
    }
  };
};

export const getOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cheryleaf.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Chery Leaf',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+441603699955',
      contactType: 'customer service',
      email: 'hello@cherryleafnorwich.co.uk',
      areaServed: 'GB',
      availableLanguage: 'English'
    },
    sameAs: [
      'https://www.instagram.com/cherryleaf_norwich',
      'https://x.com/cherryleafcoffe'
    ]
  };
};

export const getMenuSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cheryleaf.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: 'Chery Leaf Norwich Coffee & Breakfast Menu',
    description: 'Our selection of artisanal coffees, breakfast items, specialty drinks, and fresh pastries in Norwich',
    provider: {
      '@type': 'CafeOrCoffeeShop',
      name: 'Chery Leaf',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '50 St Giles Street',
        addressLocality: 'Norwich',
        addressRegion: 'Norfolk',
        postalCode: 'NR2 1LW',
        addressCountry: 'GB'
      }
    },
    hasMenuSection: [
      {
        '@type': 'MenuSection',
        name: 'Classic Coffee',
        description: 'Traditional coffee beverages made with premium beans',
        hasMenuItem: [
          { '@type': 'MenuItem', name: 'Espresso', offers: { '@type': 'Offer', price: '2.80', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Cappuccino', offers: { '@type': 'Offer', price: '3.50', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Latte', offers: { '@type': 'Offer', price: '3.80', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Americano', offers: { '@type': 'Offer', price: '3.20', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Flat White', offers: { '@type': 'Offer', price: '3.60', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Macchiato', offers: { '@type': 'Offer', price: '3.00', priceCurrency: 'GBP' } }
        ]
      },
      {
        '@type': 'MenuSection',
        name: 'Cold Drinks',
        description: 'Refreshing cold beverages',
        hasMenuItem: [
          { '@type': 'MenuItem', name: 'Iced Coffee', offers: { '@type': 'Offer', price: '3.80', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Cold Brew', offers: { '@type': 'Offer', price: '4.00', priceCurrency: 'GBP' } }
        ]
      },
      {
        '@type': 'MenuSection',
        name: 'Specialty Drinks',
        description: 'Unique specialty beverages',
        hasMenuItem: [
          { '@type': 'MenuItem', name: 'Caramel Latte', offers: { '@type': 'Offer', price: '4.50', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Vanilla Latte', offers: { '@type': 'Offer', price: '4.50', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Mocha', offers: { '@type': 'Offer', price: '4.20', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Matcha Latte', offers: { '@type': 'Offer', price: '4.80', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Chai Latte', offers: { '@type': 'Offer', price: '4.20', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Hot Chocolate', offers: { '@type': 'Offer', price: '3.80', priceCurrency: 'GBP' } }
        ]
      },
      {
        '@type': 'MenuSection',
        name: 'Breakfast & Pastries',
        description: 'Freshly baked pastries and breakfast treats',
        hasMenuItem: [
          { '@type': 'MenuItem', name: 'Croissant', offers: { '@type': 'Offer', price: '2.80', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Blueberry Muffin', offers: { '@type': 'Offer', price: '3.20', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Chocolate Cake', offers: { '@type': 'Offer', price: '4.50', priceCurrency: 'GBP' } },
          { '@type': 'MenuItem', name: 'Cinnamon Roll', offers: { '@type': 'Offer', price: '3.50', priceCurrency: 'GBP' } }
        ]
      }
    ]
  };
};

export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cheryleaf.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};
